import type { SelectedWhyBuyMedia } from './selectWhyBuyMedia';
import { getWhyBuyAssetType } from './whyBuyAnalytics';

export const WHY_BUY_SORT_PARAM = 'why-buy-sort';

type Rank = 1 | 2 | 3;
type SortableNarrative = { media?: SelectedWhyBuyMedia };

const RANK_WINDOWS: Record<Rank, { end: number; start: number }> = {
  1: { end: 0.6, start: 0 },
  2: { end: 0.8, start: 0.2 },
  3: { end: 1, start: 0.4 }
};

function getRank({ media }: SortableNarrative): Rank {
  const assetType = getWhyBuyAssetType(media);
  if (assetType === 'stock image') {
    return 1;
  }

  return assetType === 'infographic' ? 2 : 3;
}

function sortStockFirst<T extends SortableNarrative>(items: Array<T>) {
  return [...items].sort((a, b) => getRank(a) - getRank(b));
}

function pinTopThreeStock<T extends SortableNarrative>(items: Array<T>) {
  const pinned = items.filter(item => getRank(item) === 1).slice(0, 3);

  return [...pinned, ...items.filter(item => !pinned.includes(item))];
}

function spreadPlaceholders<T extends SortableNarrative>(items: Array<T>) {
  const others = items.filter(item => getRank(item) !== 3);
  const placeholders = items.filter(item => getRank(item) === 3);
  const result: Array<T> = [];

  for (let group = 0; others.length > 0; group += 1) {
    const placeholder = placeholders.shift();
    if (!placeholder) {
      result.push(...others.splice(0));
      break;
    }
    const groupItems = others.splice(0, 2);
    const placeholderSlot = Math.min(2 - (group % 3), groupItems.length);
    groupItems.splice(placeholderSlot, 0, placeholder);
    result.push(...groupItems);
  }

  return [...result, ...placeholders];
}

function pinTopThreeThenSpread<T extends SortableNarrative>(items: Array<T>) {
  const pinned = sortStockFirst(
    items.filter(item => getRank(item) !== 3)
  ).slice(0, 3);

  return [
    ...pinned,
    ...spreadPlaceholders(items.filter(item => !pinned.includes(item)))
  ];
}

function spreadByRank<T extends SortableNarrative>(items: Array<T>) {
  const counts: Record<Rank, number> = { 1: 0, 2: 0, 3: 0 };
  items.forEach(item => {
    counts[getRank(item)] += 1;
  });
  const seen: Record<Rank, number> = { 1: 0, 2: 0, 3: 0 };

  return items
    .map((item, index) => {
      const rank = getRank(item);
      const { end, start } = RANK_WINDOWS[rank];
      seen[rank] += 1;

      return {
        index,
        item,
        rank,
        score: start + ((end - start) * seen[rank]) / (counts[rank] + 1)
      };
    })
    .sort((a, b) => a.score - b.score || a.rank - b.rank || a.index - b.index)
    .map(({ item }) => item);
}

// Five sorts let product compare options via ?why-buy-sort; REO-9765 removes all but the chosen one
export default function sortWhyBuyNarratives<T extends SortableNarrative>(
  items: Array<T>,
  sort: string | null
): Array<T> {
  switch (sort) {
    case '1':
      return sortStockFirst(items);
    case '2':
      return pinTopThreeStock(items);
    case '3':
      return spreadPlaceholders(items);
    case '4':
      return spreadByRank(items);
    case '5':
      return pinTopThreeThenSpread(items);
    default:
      return items;
  }
}
